import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@marine/api-client/api';

/**
 * Platform-wide AI settings — singleton row in landlord DB. Every API
 * payload here ships masked keys only (`sk-ant-…abcd`) — raw key material
 * never traverses the wire after the admin types it in.
 */
export interface PlatformAiSettings {
  id: string;
  masked_keys: {
    anthropic:  string | null;
    openrouter: string | null;
    perplexity: string | null;
    openai:     string | null;
  };
  configured: {
    anthropic:  boolean;
    openrouter: boolean;
    perplexity: boolean;
    openai:     boolean;
  };
  updated_at: string | null;
  /** #597 — resolved server-side; raw user IDs never render in the UI. */
  updated_by: { id: string; full_name: string } | null;
}

export interface UpdatePlatformAiSettingsInput {
  anthropic_api_key?:  string | null;
  openrouter_api_key?: string | null;
  perplexity_api_key?: string | null;
  openai_api_key?:     string | null;
}

export interface TenantAiKeyOverrides {
  tenant_id:   string;
  masked_keys: {
    anthropic:  string | null;
    openrouter: string | null;
    perplexity: string | null;
    openai:     string | null;
  };
  configured:  {
    anthropic:  boolean;
    openrouter: boolean;
    perplexity: boolean;
    openai:     boolean;
  };
}

export interface UpdateTenantAiKeysInput {
  anthropic_api_key_override?:  string | null;
  openrouter_api_key_override?: string | null;
  perplexity_api_key_override?: string | null;
  openai_api_key_override?:     string | null;
}

// `api.getOne` / `api.putOne` already unwrap the `{data: ...}` envelope (see
// frontend/src/lib/api.ts), so these signatures and the hooks below DO NOT
// need a `select: (r) => r.data` step.
export const platformAiSettingsApi = {
  show:    (): Promise<PlatformAiSettings> =>
    api.getOne<PlatformAiSettings>('/api/ai/settings'),
  update:  (input: UpdatePlatformAiSettingsInput): Promise<PlatformAiSettings> =>
    api.putOne<PlatformAiSettings>('/api/ai/settings', input),

  showTenant:   (tenantId: string): Promise<TenantAiKeyOverrides> =>
    api.getOne<TenantAiKeyOverrides>(`/api/tenants/${tenantId}/ai-keys`),
  updateTenant: (tenantId: string, input: UpdateTenantAiKeysInput): Promise<TenantAiKeyOverrides> =>
    api.putOne<TenantAiKeyOverrides>(`/api/tenants/${tenantId}/ai-keys`, input),
};

export function usePlatformAiSettings() {
  return useQuery({
    queryKey: ['platform-ai-settings'],
    queryFn:  () => platformAiSettingsApi.show(),
  });
}

export function useUpdatePlatformAiSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdatePlatformAiSettingsInput) => platformAiSettingsApi.update(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform-ai-settings'] });
    },
  });
}

export function useTenantAiKeyOverrides(tenantId: string | undefined) {
  return useQuery({
    queryKey: ['platform-tenant-ai-keys', tenantId],
    queryFn:  () => platformAiSettingsApi.showTenant(tenantId!),
    enabled:  !!tenantId,
  });
}

export function useUpdateTenantAiKeyOverrides(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateTenantAiKeysInput) => platformAiSettingsApi.updateTenant(tenantId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform-tenant-ai-keys', tenantId] });
    },
  });
}
